import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { BrandProvider } from "@/context/BrandContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Catalog from "@/pages/Catalog";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import ProductForm from "@/pages/ProductForm";
import Settings from "@/pages/Settings";
import Users from "@/pages/Users";
import Purchases, { PurchaseNew, PurchaseDetail } from "@/pages/Purchases";
import PublicLanding from "@/pages/PublicLanding";
import PublicCatalog from "@/pages/PublicCatalog";
import PublicProductDetail from "@/pages/PublicProductDetail";
import Reporting from "@/pages/Reporting";
import MetaAds from "@/pages/MetaAds";
import Pos from "@/pages/Pos";
import PosGate from "@/components/pos/PosGate";
import { CATALOG_ROLES, REPORTING_ROLES, META_ADS_ROLES } from "@/lib/roles";

const CATALOG_ROLE_LIST = [...CATALOG_ROLES];

// Catalog icon is the site default; Purchasing pages keep their previous (generic) icon until a Purchasing icon is supplied.
function IconScope() {
    const { pathname } = useLocation();
    useEffect(() => {
        const purchasing = pathname.startsWith("/purchases");
        document.querySelectorAll("link[data-catalog-icon]").forEach((l) => {
            l.href = purchasing ? "data:," : l.dataset.catalogIcon;
        });
    }, [pathname]);
    return null;
}

function App() {
    return (
        <AuthProvider>
            <BrandProvider>
                <SettingsProvider>
                    <BrowserRouter>
                        <IconScope />
                        <Toaster
                            position="top-center"
                            toastOptions={{
                                className: "font-sans",
                                style: { background: "#6F7D69", color: "#F8F6F2", border: "none", borderRadius: "4px" },
                            }}
                        />
                        <Routes>
                            <Route path="/" element={<PublicLanding />} />
                            <Route path="/login" element={<Login />} />

                            {/* Public brand-specific catalogs */}
                            <Route path="/hoshizora" element={<PublicCatalog brand="hoshizora" />} />
                            <Route path="/hoshizora/:id" element={<PublicProductDetail brand="hoshizora" />} />
                            <Route path="/heelsandtea" element={<PublicCatalog brand="heelsandtea" />} />
                            <Route path="/heelsandtea/:id" element={<PublicProductDetail brand="heelsandtea" />} />

                            {/* Authenticated workspace */}
                            <Route
                                element={
                                    <ProtectedRoute>
                                        <Layout />
                                    </ProtectedRoute>
                                }
                            >
                                <Route
                                    path="/catalog"
                                    element={
                                        <ProtectedRoute roles={CATALOG_ROLE_LIST}>
                                            <Catalog />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/reporting"
                                    element={
                                        <ProtectedRoute roles={REPORTING_ROLES}>
                                            <Reporting />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/meta-ads"
                                    element={
                                        <ProtectedRoute roles={META_ADS_ROLES}>
                                            <MetaAds />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/products"
                                    element={
                                        <ProtectedRoute roles={["owner", "admin_input", "admin_sales", "admin"]}>
                                            <Products />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/products/new"
                                    element={
                                        <ProtectedRoute roles={["owner", "admin_input", "admin"]}>
                                            <ProductForm />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/products/:id"
                                    element={
                                        <ProtectedRoute roles={CATALOG_ROLE_LIST}>
                                            <ProductDetail />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/products/:id/edit"
                                    element={
                                        <ProtectedRoute roles={["owner", "admin_input", "admin"]}>
                                            <ProductForm />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/settings"
                                    element={
                                        <ProtectedRoute roles={["owner", "admin_input", "admin"]}>
                                            <Settings />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/settings/users"
                                    element={
                                        <ProtectedRoute ownerOnly>
                                            <Users />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/pos"
                                    element={
                                        <ProtectedRoute ownerOnly>
                                            <PosGate><Pos /></PosGate>
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/purchases"
                                    element={
                                        <ProtectedRoute ownerOnly>
                                            <Purchases />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/purchases/new"
                                    element={
                                        <ProtectedRoute ownerOnly>
                                            <PurchaseNew />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/purchases/:id"
                                    element={
                                        <ProtectedRoute ownerOnly>
                                            <PurchaseDetail />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>
                        </Routes>
                    </BrowserRouter>
                </SettingsProvider>
            </BrandProvider>
        </AuthProvider>
    );
}

export default App;
